import { MutableRefObject, useEffect, useState } from "react";
import styled, { css } from "styled-components";
import { hideAboveMobile, hideOnMobile, xSmallScreen } from "../lib/mediaQuery";
import { Body, Footnote, Headline, Subhead } from "./Typography";
import Checkbox from "./inputs/Checkbox";

interface TableProps {
  dataToHeadersMap: TableDataItem;
  data: TableDataItem[];
  notificationIds?: string[];
  handleRowClick?: (index?: number) => void;
  checkbox?: boolean;
  lgMinMaxCol?: string;
  smMinMaxCol?: string;
  callback?: (data?: any) => void;
  selected?: string;
  lgMaxHeight?: string;
  smMaxHeight?: string;
  ref?: MutableRefObject<HTMLDivElement>;
  tableContainerRef?: MutableRefObject<HTMLDivElement>;
}

type TableDataItem = {
  [key: string]: string | number;
};

export const Table = ({
  dataToHeadersMap,
  data,
  notificationIds,
  checkbox,
  lgMinMaxCol = `150px, 1fr`,
  smMinMaxCol = `104px, 1fr`,
  lgMaxHeight,
  smMaxHeight,
  handleRowClick,
  tableContainerRef,
  callback,
  ref,
}: TableProps) => {
  const [selectedCheckbox, setSelectedCheckbox] = useState<number>();
  const numColumns = Object?.values(dataToHeadersMap).length;

  useEffect(() => {
    if (callback) {
      callback(selectedCheckbox ?? null);
    }
  }, [selectedCheckbox]);

  return (
    <Container
      lgMaxHeight={lgMaxHeight}
      smMaxHeight={smMaxHeight}
      ref={tableContainerRef}
    >
      <StyledTable>
        {dataToHeadersMap && (
          <thead>
            <Row
              numColumns={numColumns}
              lgMinMaxCol={lgMinMaxCol}
              smMinMaxCol={smMinMaxCol}
            >
              {Object.values(dataToHeadersMap).map((header) => (
                <Header>
                  <Headline
                    css={`
                      ${hideOnMobile};
                      ${truncationStyles}
                    `}
                  >
                    {header}
                  </Headline>
                  <Subhead
                    css={`
                      ${hideAboveMobile};
                      ${truncationStyles}
                      font-weight: 600;
                    `}
                  >
                    {header}
                  </Subhead>
                </Header>
              ))}
            </Row>
          </thead>
        )}
        <tbody>
          {data?.map((item, index) => {
            return (
              <Row
                key={index}
                numColumns={numColumns}
                lgMinMaxCol={lgMinMaxCol}
                smMinMaxCol={smMinMaxCol}
                onClick={() => Boolean(handleRowClick) && handleRowClick(index)}
                data-is-clickable={Boolean(handleRowClick)}
              >
                {Object.keys(item).map((key, index) => {
                  if (dataToHeadersMap[key]) {
                    return (
                      <Cell key={key}>
                        <NotificationDot
                          data-is-alert={
                            index === 1 &&
                            notificationIds?.includes(item.id as string)
                          }
                        />
                        <Body
                          css={`
                            ${hideOnMobile};
                            ${truncationStyles}
                          `}
                        >
                          {item[key]}
                        </Body>
                        <Footnote
                          css={`
                            ${hideAboveMobile};
                            ${truncationStyles}
                          `}
                        >
                          {item[key]}
                        </Footnote>
                      </Cell>
                    );
                  }
                })}
                {checkbox && (
                  <Cell
                    css={`
                      display: flex;
                      justify-content: flex-end;
                      padding: var(--spacing-xxs) var(--spacing-sm);
                    `}
                  >
                    <Checkbox
                      name={"checkbox"}
                      value={selectedCheckbox === index}
                      onClick={() => {
                        setSelectedCheckbox(
                          selectedCheckbox === index ? undefined : index
                        );
                      }}
                    />
                  </Cell>
                )}
              </Row>
            );
          })}
        </tbody>
      </StyledTable>
    </Container>
  );
};

export const TablePanel = styled.div`
  background: var(--table-panel-bg);
  padding: var(--spacing-sm);
  border-radius: var(--spacing-xs);
  ${xSmallScreen(
    css`
      padding: var(--spacing-xs);
    `
  )}
  margin-bottom: var(--spacing-xl);
`;

const truncationStyles = css`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

const Container = styled.div<{ lgMaxHeight?: string; smMaxHeight?: string }>`
  overflow-x: auto;
  overflow-y: ${(p) => (p.lgMaxHeight || p.smMaxHeight ? `auto` : `hidden`)};
  &::-webkit-scrollbar {
    width: 0.01em;
  }
  &::-webkit-scrollbar-thumb {
    background-color: transparent;
  }
  max-height: ${(p) => (p.lgMaxHeight ? p.lgMaxHeight : `510px`)};
  width: 100%;
  ${(p) =>
    xSmallScreen(css`
      max-height: ${p.smMaxHeight ? p.smMaxHeight : `350px`};
    `)}
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Row = styled.tr<{
  numColumns: number;
  lgMinMaxCol?: string;
  smMinMaxCol?: string;
}>`
  white-space: nowrap;
  display: grid;
  align-items: center;
  grid-template-columns: ${(p) =>
    `repeat(${p.numColumns}, minmax(${p.lgMinMaxCol}))`};
  &:nth-child(even) {
    background: var(--table-alternating-bg);
    border-radius: 4px;
  }
  ${(p) =>
    xSmallScreen(css`
      grid-template-columns: repeat(${p.numColumns}, minmax(${p.smMinMaxCol}));
    `)}
  &[data-is-clickable="true"] {
    cursor: pointer;
  }
`;

const Header = styled.th`
  text-align: left;
  white-space: nowrap;
  padding: var(--spacing-sm);
  ${xSmallScreen(
    css`
      padding: var(--spacing-xs);
    `
  )};
`;

const NotificationDot = styled.div`
  display: none;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: var(--notification-table-row-color);
  margin-right: var(--spacing-xxs);
  &[data-is-alert="true"] {
    display: initial;
  }
`;

const Cell = styled.td`
  display: flex;
  align-items: center;
  padding: var(--spacing-xs);
  ${xSmallScreen(
    css`
      padding: var(--spacing-xs);
    `
  )};
`;
